import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet } from '../../utils/api';
import { usePageTitle } from '../../contexts/PageTitleContext';
import '../../css/Board/board.css';

const TYPE_LABELS = {
  notice: '공지사항',
  event: '이벤트'
};

const formatDate = (raw) => {
  if (!raw || raw === '0000-00-00 00:00:00') return '';
  const normalized = String(raw).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return String(raw).split(' ')[0] || String(raw);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const prepareBoardContentHtml = (html) => {
  const rawHtml = String(html || '');
  if (!rawHtml || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return rawHtml;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');

  doc.querySelectorAll('a[href]').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return doc.body.innerHTML;
};

export default function BoardDetail({ fixedType, basePath = '/board' }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);

  const activeType = useMemo(() => {
    if (fixedType === 'event' || fixedType === 'notice') return fixedType;
    return 'notice';
  }, [fixedType]);

  useEffect(() => {
    setPageTitle(TYPE_LABELS[activeType] || '공지사항');
  }, [activeType, setPageTitle]);

  useEffect(() => {
    let isActive = true;

    const fetchPost = async () => {
      if (!id) return;
      setIsLoading(true);
      setError('');
      try {
        const data = await apiGet('/board/get_post.php', { id });
        if (!isActive) return;
        if (data?.success) {
          setPost(data.data || null);
          setPrevPost(data.prev || null);
          setNextPost(data.next || null);
        } else {
          setPost(null);
          setPrevPost(null);
          setNextPost(null);
          setError(data?.message || '게시글을 불러오지 못했습니다.');
        }
      } catch (err) {
        if (!isActive) return;
        setError('게시글을 불러오지 못했습니다.');
        setPost(null);
        setPrevPost(null);
        setNextPost(null);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchPost();

    return () => {
      isActive = false;
    };
  }, [id]);

  const pageParam = searchParams.get('page');
  const pageSuffix = pageParam ? `?page=${pageParam}` : '';

  const handleBack = () => {
    navigate(`${basePath || '/board'}${pageSuffix}`);
  };

  if (isLoading) {
    return (
      <div className="board-page">
        <div className="board-container">
          <div className="board-status">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="board-page">
        <div className="board-container">
          <div className="board-status error">{error}</div>
          <div className="board-actions">
            <button type="button" className="board-back-button" onClick={handleBack}>
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="board-page">
        <div className="board-container">
          <div className="board-status">게시글이 없습니다.</div>
          <div className="board-actions">
            <button type="button" className="board-back-button" onClick={handleBack}>
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  const badgeLabel = TYPE_LABELS[post.type] || TYPE_LABELS[activeType] || '공지사항';
  const date = formatDate(post.published_at || post.created_at);
  const contentHtml = prepareBoardContentHtml(post.content_html);

  return (
    <div className="board-page">
      <div className="board-container">
        <button type="button" className="board-back-button" onClick={handleBack}>
          목록으로
        </button>

        <div className="board-detail-head">
          <div>
            <span className="board-badge">{badgeLabel}</span>
            {String(post.is_pinned) === '1' && <span className="board-badge pinned">고정</span>}
          </div>
          <div className="board-detail-title">{post.title}</div>
          <div className="board-detail-meta">
            <span>{date}</span>
            <span>조회 {post.view_count ?? 0}</span>
          </div>
        </div>

        <div className="board-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />

        <div className="board-nav">
          <div
            className={`board-nav-item ${prevPost ? '' : 'disabled'}`}
            role="button"
            tabIndex={prevPost ? 0 : -1}
            onClick={() => {
              if (!prevPost) return;
              navigate(`${basePath}/${prevPost.id}${pageSuffix}`);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && prevPost) {
                navigate(`${basePath}/${prevPost.id}${pageSuffix}`);
              }
            }}
          >
            <span className="board-nav-label">이전글</span>
            <span className="board-nav-title">
              {prevPost ? prevPost.title : '이전글이 없습니다.'}
            </span>
            <span className="board-nav-date">
              {prevPost ? formatDate(prevPost.published_at || prevPost.created_at) : ''}
            </span>
          </div>
          <div
            className={`board-nav-item ${nextPost ? '' : 'disabled'}`}
            role="button"
            tabIndex={nextPost ? 0 : -1}
            onClick={() => {
              if (!nextPost) return;
              navigate(`${basePath}/${nextPost.id}${pageSuffix}`);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && nextPost) {
                navigate(`${basePath}/${nextPost.id}${pageSuffix}`);
              }
            }}
          >
            <span className="board-nav-label">다음글</span>
            <span className="board-nav-title">
              {nextPost ? nextPost.title : '다음글이 없습니다.'}
            </span>
            <span className="board-nav-date">
              {nextPost ? formatDate(nextPost.published_at || nextPost.created_at) : ''}
            </span>
          </div>
        </div>

        <div className="board-actions">
          <button type="button" className="board-back-button" onClick={handleBack}>
            목록으로
          </button>
        </div>
      </div>
    </div>
  );
}
